<template>
  <div class="step-upload-content">
    <!-- Header main -->
    <div class="header-main step-upload-content__header-main">
      <h1 class="global-h1 header-main__title">Upload Photos for your Social Posts</h1>

      <div class="header-main__subtitle">These photo groups will be the basis for your content calendar.</div>

      <HowContentCalendarWorksSpoiler class="header-main__how-does-it-work-spoiler" />

      <div v-if="!props.isLoading" style="gap: 20px; display: flex; flex-direction: column">
        <FutureContentNeeds
          v-if="hasFutureCalendarData"
          :nextCalendarGenSummary="nextCalendarGenSummary"
          :longTermCalendarGenSummary="longTermCalendarGenSummary"
          :customerId="props.customerId"
          :nextPostGenOnWeek="nextPostGenOnWeek"
          :hideActions="true"
          :hidePostGuidance="true"
        />
        <NoteWithIcon v-else color="green" class="upload-photos-note">
          <template #icon>
            <div class="upload-photos-note__icon-wrap">
              <IconEmbedded name="image_2" :size="28" color="#2f5f34" />
            </div>
          </template>
          <template #text>
            <div class="upload-photos-note__content">
              <h4 class="global-h4 upload-photos-note__title">Upload your photos to get started</h4>
              <p class="global-p upload-photos-note__intro">Your existing groups work great. Re-organizing not required.</p>
              <ul class="upload-photos-note__checks">
                <li class="upload-photos-note__check-item">
                  <IconEmbedded name="check_3" :size="15" color="#3f7434" />
                  <span>Ex: location, event, season groups are common</span>
                </li>
                <li class="upload-photos-note__check-item">
                  <IconEmbedded name="check_3" :size="15" color="#3f7434" />
                  <span>Upload {{ props.minimumAssetsForInitialPostGen.toLocaleString("en-US") }}+ photos to get started (max {{ MAX_ASSETS_PER_GROUP.toLocaleString("en-US") }} per group)</span>
                </li>
              </ul>
              <div class="upload-photos-note__divider"></div>
              <p class="global-p upload-photos-note__support">
                Contact us anytime at
                <!--<IconEmbedded class="upload-photos-note__support-icon" name="envelope_2" :size="16" color="#2f6f7b" />-->
                <CopyTextNew>support@shrpa.com</CopyTextNew>
              </p>
            </div>
          </template>
        </NoteWithIcon>
      </div>

      <div v-if="!props.isLoading" class="bulk-upload-options">
        <div class="bulk-upload-options__card bulk-upload-options__card--bulk">
          <div class="bulk-upload-options__icon-circle bulk-upload-options__icon-circle--teal">
            <IconEmbedded name="folder_2" :size="26" color="#0d686b" />
          </div>
          <div class="bulk-upload-options__body">
            <h3 class="bulk-upload-options__title">Bulk Upload</h3>
            <p class="bulk-upload-options__desc global-p">Upload an entire folder tree of photos and groups at once.</p>
            <SrpButton
              class="bulk-upload-options__cta"
              color="teal"
              fill="outlined"
              sidePaddings="small"
              :to="{ name: 'BulkAssetUploadPage', params: { customerId: props.customerId }, query: { returnTo: BULK_UPLOAD_RETURN_TO.atlas } }"
            >
              Start Bulk Upload
              <template #iconRight><IconEmbedded name="upload_3" :size="17" /></template>
            </SrpButton>
          </div>
        </div>

        <div class="bulk-upload-options__or" aria-hidden="true">
          <span class="bulk-upload-options__or-line"></span>
          <span class="bulk-upload-options__or-badge">OR</span>
          <span class="bulk-upload-options__or-line"></span>
        </div>

        <div class="bulk-upload-options__card bulk-upload-options__card--manual">
          <div class="bulk-upload-options__icon-circle bulk-upload-options__icon-circle--blue">
            <IconEmbedded name="hand-3-fingers_1-5" :size="26" color="#1565c0" />
          </div>
          <div class="bulk-upload-options__body">
            <h3 class="bulk-upload-options__title">Build Groups Yourself</h3>
            <p class="bulk-upload-options__desc global-p">Create groups and organize uploaded photos yourself.</p>
            <a href="#" class="bulk-upload-options__scroll-link" @click.prevent="scrollToPhotosSection">↓ Create a group to get started</a>
          </div>
        </div>
      </div>
    </div>
    <!-- / Header main -->

    <div v-if="props.isLoading" style="margin-bottom: 35px; display: flex; flex-direction: column; align-items: center; text-align: center">
      <LoaderNew style="width: 70px; height: 70px; margin-bottom: 10px" :borderWidth="3" />
      <div class="global-p">
        <h2>Loading...</h2>
      </div>
    </div>

    <template v-else>
      <div ref="photosSectionRef" class="step-upload-content__photos-anchor">
        <AllPhotosSection
          :customerId="props.customerId"
          :onlyShowCustomerUploads="true"
          :isVideoUploadAllowed="false"
          :showTotalAssetCount="false"
          :showBulkUploadButton="false"
          :currentOperationSource="hasFutureCalendarData ? PhotoGallerySource.CalendarAddingContent : PhotoGallerySource.CalendarManual"
          @dataLoaded="handleDataLoaded"
          @galleryGroupsUpdated="handleGalleryGroupsUpdated"
          @anyUploadingChanged="val => (isAnyUploadInProgress = val)"
        />
      </div>

      <!-- Manage buttons floating panel -->
      <div class="manage-buttons-floating-panel step-upload-content__manage-buttons-floating-panel">
        <div class="manage-buttons-floating-panel__side-left"></div>

        <div class="manage-buttons-floating-panel__side-right">
          <!--<SrpButton class="manage-buttons-floating-panel__button" color="gray" fill="outlined" @click="emit('goBack')" :isDisabled="isLoading" iconInset="0 auto auto auto">
            <template #icon><IconEmbedded name="arrow-left_3-5" :size="17" /></template>
            Go back
          </SrpButton>-->

          <!--<div v-if="getTotalPhotos() > 0 && !hasFutureCalendarData" class="manage-buttons-floating-panel__photo-count-info">
            <div class="photo-count-info__photos">{{ getTotalPhotos() }} photos</div>
            <div class="photo-count-info__groups">in {{ getGroupsWithPhotos() }} {{ getGroupsWithPhotos() === 1 ? "group" : "groups" }}</div>
          </div>-->

          <div v-if="photosNeededToProceed > 0 && !hasFutureCalendarData" class="manage-buttons-floating-panel__photos-needed">
            {{ photosNeededLabel }}
          </div>

          <SrpButton
            class="manage-buttons-floating-panel__button"
            color="teal"
            :fill="getProceedButtonFill()"
            @click="handleProceedClick"
            :isDisabled="isLoading || !shouldShowProceedButton() || isProceeding || isAnyUploadInProgress"
            iconInset="0 auto auto auto"
          >
            <template #iconRight><IconEmbedded name="arrow-right_3-5" :size="17" /></template>
            {{ hasFutureCalendarData ? "Done" : "Proceed" }}
          </SrpButton>
        </div>
      </div>
      <!-- / Manage buttons floating panel -->
    </template>

    <!-- Leave page warning modal -->
    <SrpModal v-model:isVisible="isLeavePageWarningVisible" :isClosable="true" :isCloseButtonVisible="true" size="medium">
      <template #content>
        <div class="leave-page-warning-modal">
          <div class="standard-page-header standard-page-header--no-bottom-line leave-page-warning-modal__header">
            <h2 class="global-h2 standard-page-header__title">Uploading in progress</h2>
          </div>

          <div class="global-p" style="margin-top: 10px">We're still uploading your files. Leaving this page now may stop the upload.</div>
        </div>
      </template>

      <template #footer="{ closeModal }">
        <SrpButton @click="closeModal">Finish uploading</SrpButton>
        <SrpButton color="gray" fill="outlined" @click="handleLeavePage">Leave page</SrpButton>
      </template>
    </SrpModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject } from "vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";

const globalRemoteLogger = inject("globalRemoteLogger") as any;
const router = useRouter();

// Components
import NoteWithIcon from "@components/NoteWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import AllPhotosSection from "@components/AllPhotosSection/index.vue";
import LoaderNew from "@components/Loader/LoaderNew.vue";
import HowContentCalendarWorksSpoiler from "@views/Community/ContentCalendar/HowContentCalendarWorksSpoiler.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import FutureContentNeeds from "../FutureContentNeeds.vue";
import CopyTextNew from "@components/CopyTextNew.vue";

// Types
import { PhotoGallerySource, PhotoGalleryCustomerData, MAX_ASSETS_PER_GROUP } from "@contracts/photosPageData";
import { BULK_UPLOAD_RETURN_TO } from "@views/Community/BulkAssetUpload/bulkUploadReturnTo";
import { CalendarGenResultSummaryData } from "@contracts/contentCalendar";

const props = withDefaults(
  defineProps<{
    customerId: string;
    galleryGroups: Array<PhotoGalleryCustomerData>;
    minimumAssetsForInitialPostGen: number;
    isLoading: boolean;
  }>(),
  {}
);

const emit = defineEmits<{
  (e: "proceed"): void;
  (e: "update:galleryGroups", updatedGalleryGroups: Array<PhotoGalleryCustomerData>): void;
}>();

// Future calendar data =======================================================
const nextCalendarGenSummary = ref<CalendarGenResultSummaryData | null>(null);
const longTermCalendarGenSummary = ref<CalendarGenResultSummaryData | null>(null);
const nextPostGenOnWeek = ref<string | null>(null);

const hasFutureCalendarData = computed<boolean>(() => {
  return Boolean(nextCalendarGenSummary.value || longTermCalendarGenSummary.value);
});

// Fetch calendar data on mount
onMounted(async () => {
  const calendarData = await ContentCalendarRepo.getNextFutureCalendarSummary(props.customerId);
  nextCalendarGenSummary.value = calendarData.nextCalendarGenSummary;
  longTermCalendarGenSummary.value = calendarData.longTermCalendarGenSummary;
  nextPostGenOnWeek.value = null; // Not provided by this endpoint
});

// Track if user has taken any action with gallery groups
const isActionTaken = ref<boolean>(false);

// Computed values for button state ===========================================
function getTotalPhotos(): number {
  return props.galleryGroups.reduce((sum, g) => sum + g.photoIds.length, 0);
}

function getGroupsWithPhotos(): number {
  return props.galleryGroups.filter(g => g.photoIds.length > 0).length;
}

const photosNeededToProceed = computed<number>(() => Math.max(props.minimumAssetsForInitialPostGen - getTotalPhotos(), 0));

const photosNeededLabel = computed<string>(() => {
  // const photoText = photosNeededToProceed.value === 1 ? "photo" : "photos";
  return `${photosNeededToProceed.value.toLocaleString("en-US")} more needed`;
});

function getProceedButtonFill(): "outlined" | "solid" {
  return hasFutureCalendarData.value || photosNeededToProceed.value === 0 ? "solid" : "outlined";
}

function shouldShowProceedButton(): boolean {
  return hasFutureCalendarData.value || photosNeededToProceed.value === 0;
}

const isProceeding = ref<boolean>(false);
const isAnyUploadInProgress = ref<boolean>(false);

const photosSectionRef = ref<HTMLElement | null>(null);

function scrollToPhotosSection() {
  photosSectionRef.value?.scrollIntoView({ behavior: "smooth", block: "start" });
}

// Navigation warning when uploads are in progress ===========================
const isLeavePageWarningVisible = ref<boolean>(false);
const pendingNavigation = ref<any>(null);

async function handleProceedClick() {
  isProceeding.value = true;

  // Skip finishedUploading call if nothing changed.
  // This prevents unnecessary reprocessing for live calendars since content updates are cross-calendar.
  var skipApiCall = !isActionTaken.value && hasFutureCalendarData.value;
  if (skipApiCall) globalRemoteLogger.info(`ContentCalendar: Skipped finishedUploading, no changes. customerId=${props.customerId}`, true);
  else await ContentCalendarRepo.finishedUploading(props.customerId);

  emit("proceed");
}

// Handle data loaded from AllPhotosSection ==================================
function handleDataLoaded(data: any) {
  if (data.customerGalleryPhotoGroups) {
    emit("update:galleryGroups", data.customerGalleryPhotoGroups);
  }
}

// Handle gallery groups updated from AllPhotosSection =======================
function handleGalleryGroupsUpdated(galleryGroups: Array<PhotoGalleryCustomerData>) {
  isActionTaken.value = true;
  emit("update:galleryGroups", galleryGroups);
}

// Navigation guard to warn if uploads are in progress =======================
onBeforeRouteLeave(to => {
  if (isAnyUploadInProgress.value) {
    // Block navigation and show warning modal
    pendingNavigation.value = to;
    isLeavePageWarningVisible.value = true;
    return false;
  }
  // Allow navigation
  return true;
});

function handleLeavePage() {
  isLeavePageWarningVisible.value = false;
  if (pendingNavigation.value) {
    // Temporarily set to false to allow navigation through the guard
    const wasUploading = isAnyUploadInProgress.value;
    isAnyUploadInProgress.value = false;
    router.push(pendingNavigation.value).catch(() => {
      // If navigation fails for any reason, restore the state
      isAnyUploadInProgress.value = wasUploading;
    });
  }
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "./headers.scss";

// Manage buttons floating panel ==============================================
.manage-buttons-floating-panel {
  gap: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 0 20px 20px rgba(255, 255, 255, 1);
  background: rgba(255, 255, 255, 1);

  &__side-left {
    gap: 12px;
    display: flex;
    align-items: center;
  }

  &__side-right {
    gap: 12px;
    display: flex;
    align-items: center;
  }

  &__photo-count-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-size: 14px;
    line-height: 1.3;
    color: #333;
    font-weight: 600;
  }

  &__photos-needed {
    font-size: 14px;
    line-height: 1.3;
    font-weight: 700;
    // color: #0d686b;
    white-space: nowrap;
  }
}

// Step upload content ========================================================
.step-upload-content {
  &__header-main {
    margin-bottom: 35px;
  }

  &__add-section-button {
    margin-bottom: 30px;
  }

  &__divider {
    margin-bottom: 30px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__manage-buttons-floating-panel {
    margin-top: 25px;
    margin-bottom: 35px;
    position: sticky;
    inset: auto auto 15px 0;
  }

  &__photos-anchor {
    scroll-margin-top: 24px;
  }
}

.upload-photos-note {
  max-width: 900px;
  align-items: stretch;

  &__icon-wrap {
    width: 54px;
    min-width: 54px;
    height: 54px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #d8ebcb;
  }

  &__content {
    width: 100%;
    padding: 2px 0;
  }

  &__title {
    margin: 0;
    font-size: 20px;
    line-height: 1.25;
    color: #1f3320;
  }

  &__intro {
    margin: 9px 0 0;
    font-size: 16px;
    line-height: 1.35;
    color: #243a23;
  }

  &__checks {
    margin: 10px 0 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__check-item {
    gap: 10px;
    display: flex;
    align-items: flex-start;
    font-size: 15px;
    line-height: 1.35;
    color: #1f3320;
  }

  &__subline {
    display: block;
    margin-top: 2px;
    font-size: 14px;
    color: #365c32;
  }

  &__divider {
    margin: 12px 0 10px;
    border-top: 2px solid #e4eee0;
  }

  &__support {
    margin: 0;
    font-size: 15px;
    line-height: 1.35;
    color: #1f3320;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__support-icon {
    position: relative;
    top: 1px;
  }

  :deep(.copy-text-new) {
    color: #1f3320;
    text-decoration: none;
  }
}

// Bulk upload vs manual grouping cards =======================================
.bulk-upload-options {
  box-sizing: border-box;
  gap: 20px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  width: 100%;
  margin-top: 20px;

  &__card {
    box-sizing: border-box;
    gap: 16px;
    display: flex;
    align-items: center;
    min-width: 0;
    padding: 20px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    background: #fff;
  }

  &__icon-circle {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;

    &--teal {
      background: rgba(13, 104, 107, 0.12);
    }

    &--blue {
      background: rgba(21, 101, 192, 0.12);
    }
  }

  &__body {
    gap: 8px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.25;
    color: #111;
  }

  &__desc {
    margin: 0;
    font-size: 15px;
    line-height: 1.4;
    color: #555;
  }

  &__cta {
    margin-top: 4px;
  }

  &__scroll-link {
    margin-top: 4px;
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
    color: #0d686b;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__or {
    box-sizing: border-box;
    gap: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 46px;
    min-width: 46px;
    align-self: stretch;
    min-height: 100%;
  }

  &__or-line {
    flex: 1 1 0;
    box-sizing: border-box;
    width: 0;
    min-height: 6px;
    border-left: 1px solid rgba(0, 0, 0, 0.15);
  }

  &__or-badge {
    flex-shrink: 0;
    box-sizing: border-box;
    width: 40px;
    height: 40px;
    margin-top: 5px;
    margin-bottom: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    font:
      700 12px/1 "Quicksand",
      sans-serif;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.75);
    background: rgba(220, 226, 230, 1);
  }
}

@media (max-width: $mobile-max-width) {
  .upload-photos-note {
    &__icon-wrap {
      width: 42px;
      min-width: 42px;
      height: 42px;
    }

    &__title {
      font-size: 18px;
    }

    &__intro {
      font-size: 15px;
      line-height: 1.3;
    }

    &__check-item {
      font-size: 14px;
      line-height: 1.35;
    }

    &__support {
      font-size: 14px;
      line-height: 1.35;
    }
  }

  .bulk-upload-options {
    grid-template-columns: 1fr;
  }

  .bulk-upload-options__or {
    flex-direction: row;
    width: 100%;
    min-width: 0;
    height: 46px;
    min-height: 46px;
    align-self: center;
  }

  .bulk-upload-options__or-line {
    flex: 1 1 0;
    width: auto;
    min-width: 6px;
    height: 0;
    min-height: 0;
    border-left: none;
    border-top: 1px solid rgba(0, 0, 0, 0.15);
    align-self: center;
  }

  .bulk-upload-options__or-badge {
    margin-top: 0;
    margin-bottom: 0;
    margin-left: 5px;
    margin-right: 5px;
  }
}
</style>
